import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCourses, useDeleteCourse, useTeachers } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  BookOpen, 
  Loader2, 
  MoreHorizontal,
  Eye,
  Pencil,
  Archive,
  Trash2,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Users,
  Calendar,
  Clock
} from 'lucide-react';
import CourseForm from '../../components/courses/CourseForm';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const levelColors = {
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const statusColors = {
  upcoming: 'bg-blue-100 text-blue-700',
  active: 'bg-success/10 text-success',
  completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-destructive/10 text-destructive',
};

const statusLabelsFn = (lang = 'fr') => ({
  upcoming: lang === 'de' ? 'Bevorstehend' : 'À venir',
  active: lang === 'de' ? 'Aktiv' : 'Actif',
  completed: lang === 'de' ? 'Abgeschlossen' : 'Terminé',
  cancelled: lang === 'de' ? 'Abgebrochen' : 'Annulé',
});

const CourseList = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    level: '',
    teacher_id: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const canCreate = isAdmin || isRespPeda;
  const canEdit = isAdmin || isRespPeda;
  const canDelete = isAdmin;

  const { data, isLoading, error } = useCourses({ 
    page, 
    per_page: 20,
    search: filters.search || undefined,
    status: filters.status || undefined,
    level: filters.level || undefined,
    teacher_id: filters.teacher_id || undefined,
  });

  const { data: teachers } = useTeachers();
  const deleteCourse = useDeleteCourse();

  const handleOpenForm = (course = null) => {
    setSelectedCourse(course);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedCourse(null);
  };

  const handleDelete = async () => {
    if (!courseToDelete) return;
    try {
      await deleteCourse.mutateAsync(courseToDelete.id);
      toast.success(lang === 'de' ? 'Kurs erfolgreich gelöscht' : 'Cours supprimé avec succès');
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    } catch (error) {
      toast.error(lang === 'de' ? 'Fehler beim Löschen' : 'Erreur lors de la suppression');
    }
  };

  const resetFilters = () => {
    setFilters({ search: '', status: '', level: '', teacher_id: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.status || filters.level || filters.teacher_id;
  const statusLabels = statusLabelsFn(lang);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('courses_title', lang)}</h1>
          {data && (
            <Badge variant="secondary" className="text-base px-3">
              {data.total}
            </Badge>
          )}
        </div>
        {canCreate && (
          <Button onClick={() => handleOpenForm()} className="btn-active" data-testid="add-course-btn">
            <Plus className="w-4 h-4 mr-2" />
            {lang === 'de' ? 'Neuer Kurs' : 'Nouveau cours'}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t("courses_search", lang)}
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setPage(1);
                  }}
                  className="pl-10"
                  data-testid="course-search-input"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={hasActiveFilters ? 'border-primary text-primary' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Filter' : 'Filtres'}
                {hasActiveFilters && <Badge className="ml-2 bg-primary text-white">!</Badge>}
                {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
            </div>

            {showFilters && (
              <div className="flex flex-col md:flex-row gap-4 pt-4 border-t animate-slide-up">
                <Select 
                  value={filters.level} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, level: value === 'all' ? '' : value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-40" data-testid="level-filter">
                    <SelectValue placeholder={lang === 'de' ? 'Niveau' : 'Niveau'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'de' ? 'Alle Niveaus' : 'Tous niveaux'}</SelectItem>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="B1">B1</SelectItem>
                    <SelectItem value="B2">B2</SelectItem>
                    <SelectItem value="C1">C1</SelectItem>
                    <SelectItem value="C2">C2</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.status} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, status: value === 'all' ? '' : value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                    <SelectValue placeholder={lang === 'de' ? 'Status' : 'Statut'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'de' ? 'Alle Status' : 'Tous statuts'}</SelectItem>
                    <SelectItem value="upcoming">{lang === 'de' ? 'Bevorstehend' : 'À venir'}</SelectItem>
                    <SelectItem value="active">{lang === 'de' ? 'Aktiv' : 'Actif'}</SelectItem>
                    <SelectItem value="completed">{lang === 'de' ? 'Abgeschlossen' : 'Terminé'}</SelectItem>
                    <SelectItem value="cancelled">{lang === 'de' ? 'Abgebrochen' : 'Annulé'}</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.teacher_id} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, teacher_id: value === 'all' ? '' : value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-48" data-testid="teacher-filter">
                    <SelectValue placeholder={lang === 'de' ? 'Lehrkraft' : 'Enseignant'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'de' ? 'Alle Lehrkräfte' : 'Tous enseignants'}</SelectItem>
                    {teachers?.map((teacher) => (
                      <SelectItem key={teacher.id} value={teacher.id}>
                        {teacher.first_name} {teacher.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="ghost" onClick={resetFilters} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-2" />
                  {lang === 'de' ? 'Zurücksetzen' : 'Réinitialiser'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Courses Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-destructive">
          <p>{lang === 'de' ? 'Fehler beim Laden der Kurse' : 'Erreur lors du chargement des cours'}</p>
        </div>
      ) : data?.courses?.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="font-heading text-xl font-semibold mb-1">{lang === 'de' ? 'Keine Kurse gefunden' : 'Aucun cours trouvé'}</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters || filters.search 
              ? (lang === 'de' ? 'Ändern Sie Ihre Filter, um mehr Ergebnisse anzuzeigen' : 'Modifiez vos filtres pour voir plus de résultats')
              : (lang === 'de' ? 'Erstellen Sie Ihren ersten Kurs, um zu beginnen' : 'Créez votre premier cours pour commencer')
            }
          </p>
          {canCreate && !hasActiveFilters && !filters.search && (
            <Button onClick={() => handleOpenForm()}>
              <Plus className="w-4 h-4 mr-2" />
              {lang === 'de' ? 'Kurs erstellen' : 'Créer un cours'}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data?.courses?.map((course) => (
            <Card 
              key={course.id} 
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/courses/${course.id}`)}
              data-testid={`course-card-${course.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="font-heading text-lg line-clamp-1 group-hover:text-primary transition-colors">
                      {course.name}
                    </CardTitle>
                    {course.description && (
                      <CardDescription className="line-clamp-2 mt-1">
                        {course.description}
                      </CardDescription>
                    )}
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/courses/${course.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          {lang === 'de' ? 'Ansehen' : 'Voir'}
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={() => handleOpenForm(course)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            {lang === 'de' ? 'Bearbeiten' : 'Modifier'}
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setCourseToDelete(course);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {lang === 'de' ? 'Löschen' : 'Supprimer'}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={levelColors[course.level]}>
                    {course.level}
                  </Badge>
                  <Badge className={statusColors[course.status]}>
                    {statusLabels[course.status]}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Teacher */}
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-secondary text-white text-xs">
                      {course.teacher_name ? course.teacher_name.split(' ').map(n => n[0]).join('') : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-muted-foreground">
                    {course.teacher_name || (lang === 'de' ? 'Nicht zugewiesen' : 'Non assigné')}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span>{course.enrolled_count} / {course.max_students}</span>
                  </div>
                  {course.schedule && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span className="truncate">{course.schedule}</span>
                    </div>
                  )}
                </div>

                {/* Sessions Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{lang === 'de' ? 'Sitzungen' : 'Sessions'}</span>
                    <span className="font-medium">
                      {course.completed_sessions} / {course.sessions_count}
                    </span>
                  </div>
                  <Progress 
                    value={course.sessions_count > 0 ? (course.completed_sessions / course.sessions_count) * 100 : 0} 
                    className="h-2" 
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Course Form Sheet */}
      <CourseForm 
        open={formOpen} 
        onClose={handleCloseForm} 
        course={selectedCourse} 
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{lang === 'de' ? 'Löschung bestätigen' : 'Confirmer la suppression'}</DialogTitle>
            <DialogDescription>
              {lang === 'de' ? `Sind Sie sicher, dass Sie den Kurs "${courseToDelete?.name}" löschen möchten? Diese Aktion löscht auch alle zugehörigen Sitzungen, Materialien und Noten.` : `Êtes-vous sûr de vouloir supprimer le cours "${courseToDelete?.name}"? Cette action supprimera également toutes les sessions, matériaux et notes associés.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {lang === 'de' ? 'Abbrechen' : 'Annuler'}
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              {lang === 'de' ? 'Löschen' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseList;
